// frontend/src/hooks/useApi.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getBackendUrl } from '../config';

// =========================================================================
// ESCUDO DE SEGURANÇA E REDIRECIONADOR MÁGICO
// Engana o sistema inteiro: sempre que alguma tela pedir o 'token' no localStorage,
// nós puxamos do sessionStorage (seguro). O restante dos dados continua normal.
// =========================================================================
if (!window.__token_override_applied__) {
    const origGet = localStorage.getItem.bind(localStorage);
    localStorage.getItem = function(key) {
        if (key === 'token') return sessionStorage.getItem('token');
        return origGet(key);
    };
    
    const origSet = localStorage.setItem.bind(localStorage);
    localStorage.setItem = function(key, value) {
        if (key === 'token') {
            sessionStorage.setItem('token', value);
            return;
        }
        origSet(key, value);
    };

    const origRemove = localStorage.removeItem.bind(localStorage);
    localStorage.removeItem = function(key) {
        if (key === 'token') {
            sessionStorage.removeItem('token');
            return;
        }
        origRemove(key);
    };
    
    window.__token_override_applied__ = true;
}

// --- Hook customizado para chamadas de API ---
const useApi = () => {
    const [token, setToken] = useState(() => sessionStorage.getItem('token'));
    const [currentUser, setCurrentUser] = useState(null);

    // =========================================================================
    // SINCRONIZAÇÃO ENTRE ABAS (Para Laudos e PDFs abrirem logados)
    // =========================================================================
    useEffect(() => {
        const handleStorageEvent = (event) => {
            if (event.key === '_pedir_token_' && sessionStorage.getItem('token')) {
                localStorage.setItem('_entregar_token_', sessionStorage.getItem('token'));
                localStorage.removeItem('_entregar_token_');
            }
            if (event.key === '_entregar_token_' && event.newValue) {
                sessionStorage.setItem('token', event.newValue);
                setToken(event.newValue);
            }
        };

        window.addEventListener('storage', handleStorageEvent);

        if (!sessionStorage.getItem('token')) {
            localStorage.setItem('_pedir_token_', Date.now().toString());
            localStorage.removeItem('_pedir_token_');
        }

        return () => window.removeEventListener('storage', handleStorageEvent);
    }, []);

    useEffect(() => {
        const currentToken = sessionStorage.getItem('token');
        if (currentToken) {
            try {
                const decoded = jwtDecode(currentToken);
                setCurrentUser(decoded);
            } catch (e) {
                console.error("Token inválido:", e);
                sessionStorage.removeItem('token');
                setToken(null);
                setCurrentUser(null);
            }
        } else {
            setCurrentUser(null);
        }
    }, [token]);

    const saveToken = (userToken) => {
        sessionStorage.setItem('token', userToken);
        setToken(userToken);
    };

    const removeToken = () => {
        sessionStorage.removeItem('token');
        setToken(null);
        setCurrentUser(null);
    };

    const apiFetch = useCallback(async (endpoint, options = {}) => {
        const currentToken = sessionStorage.getItem('token');
        const headers = options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' };
        
        if (currentToken) headers['Authorization'] = `Bearer ${currentToken}`;

        const config = { ...options, headers };

        if (options.body && !(options.body instanceof FormData)) {
            config.body = JSON.stringify(options.body);
        }

        const baseUrl = getBackendUrl();
        const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const fullUrl = `${baseUrl}/api${cleanEndpoint}`;

        const response = await fetch(fullUrl, config);

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: response.statusText }));
            
            if (errorData.error === 'MONITORAMENTO_INTERROMPIDO') {
                alert("⚠️ ACESSO BLOQUEADO: O software de monitoramento foi encerrado ou não está ativo no seu PC.");
                sessionStorage.removeItem('token');
                localStorage.removeItem('token');
                window.location.reload();
                throw new Error(errorData.error);
            }

            throw new Error(errorData.error || `API Error: ${response.statusText}`);
        }
        
        if (response.status === 204 || (response.status === 200 && response.headers.get('content-length') === '0')) {
            return;
        }
        return response.json();
    }, []);

    const permissions = useMemo(() => currentUser?.permissions || {}, [currentUser]);
    const userProfile = currentUser;

    return { apiFetch, saveToken, token, removeToken, currentUser, userProfile, permissions };
};

export default useApi;